import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from 'classnames';
import _ from 'lodash';

import { Button, Dropdown, Modal, Row, Col, Popover, OverlayTrigger } from "react-bootstrap";
import { searchPages } from "actions/pages";
import { fetchPagePerformanceMetrics, fetchPagePerformanceHistory, invalidatePagePerformance } from "actions/storyboard";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import moment from 'moment';
import statsData from './stats.json';
import { INTERACTIVE_CONTENT_TYPES } from "constants/storyboardType";

import styles from './performance.module.scss';
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_SITE_PERFORMANCE } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { getPageViewedEventPayload } from "services/mixpanelUtil";

const PerformanceView = ({ storyboard }) => {
  const dispatch = useDispatch();

  const [ device, setDevice ] = useState('desktop');
  const [ selectedPage, setSelectedPage] = useState(null);
  const [ viewHistoryModal, setViewHistoryModal] = useState(false);

  const storyboardId = _.get(storyboard, 'id', null);
  const loading = useSelector((state) => _.get(state, 'storyboards.storyboard.performance.performanceMetrics.loading', false));
  const fetched = useSelector(state => _.get(state, 'pages.fetched', false));
  const performanceMetrics = useSelector((state) => _.get(state, `storyboards.storyboard.performance.performanceMetrics.items.${device}`, null));
  const lastTestedDate = useSelector((state) => _.get(state, `storyboards.storyboard.performance.performanceMetrics.items.lastTestedDate`, null));
  const error = useSelector((state) => _.get(state, 'storyboards.storyboard.performance.performanceMetrics.error', null));
  const storyboardName = useSelector(state => _.get(state, 'storyboards.storyboard.item.name', null));
  const storyboardType = useSelector(state => _.get(state, 'storyboards.storyboard.item.type', null));
  
  let pages = useSelector((state) => _.get(state, 'pages.items', []));

  const publishedPages = useMemo(() => {
    if (_.size(pages) > 0 && (pages[0].storyboard.id === storyboardId)) {
      return pages.filter(page => page.published);
    }
    return [];
  }, [pages, storyboardId]);
  
  const switchDevice = (currentDevice) => setDevice(currentDevice);
  const showHistory = (status) => setViewHistoryModal(status);

  useEffect(()=>{
    if(!selectedPage && _.size(publishedPages) > 0){
      setSelectedPage(publishedPages[0]);
    }
  },[publishedPages])

  useEffect(()=>{
    if(selectedPage){
      const test = false
      handleTestAgain(selectedPage, test);
    }
  },[selectedPage])

  const fetchPages = () => {
    if (storyboard) {
      var request = {
        storyboard: storyboard.id
      }
      
      dispatch(searchPages(request, 0, 50));
    }
  }

  useEffect(() => {
    if (storyboardId) {
      fetchPages();
    }
  }, [storyboardId]);

  useEffect(() => {
    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_SITE_PERFORMANCE, storyboardId, storyboardName, storyboardType)
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
    
    return () => {
      dispatch(invalidatePagePerformance());
    };
  }, []);

  const handleTestAgain = (currentPage, test) => {  
    const pageId = _.get(currentPage, 'id', null);

    if(pageId){
      dispatch(fetchPagePerformanceMetrics(pageId, test));
    }
  }

  const parseValue = (value) => {
    if (typeof value === 'string') {
      return parseFloat(value.replace(/,/g, ''));
    }
    return value;
  };

  const updatedStats = (stats, metrics) => {
    const metricsMap = {};
    for (const key in metrics) {
        if (metrics.hasOwnProperty(key)) {
            metricsMap[key] = metrics[key];
        }
    }

    const calculateStatus = (id, value, rangeGuide) => {
      if (!value){
        return "unknown";
      }
      let numericValue = parseValue(value);

      if (id === "performance") {
        numericValue *= 100;
      }

      if (numericValue >= rangeGuide.good[0] && numericValue <= rangeGuide.good[1]) {
          return "good";
      } else if (numericValue >= rangeGuide.fair[0] && numericValue <= rangeGuide.fair[1]) {
          return "fair";
      } else if (numericValue >= rangeGuide.poor[0] && numericValue <= rangeGuide.poor[1]) {
          return "poor";
      } else {
          return "unknown";
      }
    };

    return stats.map(stat => {
      let updatedStat = { ...stat };

      if (metricsMap.hasOwnProperty(stat.id)) {
          const metric = metricsMap[stat.id];
          const newStatus = calculateStatus(stat.id, metric.value, stat.rangeGuide);
          let scoreValue = metric.value;

          if (_.get(stat,'id', null) === "performance") {
            scoreValue = parseValue(metric.value) * 100;
            scoreValue = Math.round(scoreValue);
          }

          scoreValue = String(scoreValue).replace(/\s+/g, '');

          updatedStat = {
              ...updatedStat,
              score: scoreValue,
              status: newStatus,
              title: stat.id === 'performance' ? `${_.capitalize(device)} Performance Score` : stat.title,
              description: stat.id === 'performance' ? `An overall score for your site's performance on ${device} devices.` : stat.description
          };
      }

      return updatedStat;
    });
};

  const pagePerformanceMetrics = updatedStats(statsData, performanceMetrics);

  return (
    <>
      <div className={styles.performanceContent}>
        <header className={styles.contentHeader}>
          <div>
            <div className={styles.contentTitle}>Performance</div>
            <div className={styles.description}>A telescopic view of how your content is performing</div>
          </div>    
        </header>

        <main className={styles.tabContainer}>
          <div className={styles.contentRow}>
            <header className={styles.header}>
              <h6 className={styles.heading}>Site Performance</h6>
              <p className={styles.subheading}>This is how Google estimates your site speed in lab conditions.</p>
            </header>

            {loading &&
              <LoadingMessage/>
            }

            {error && !loading &&
              <div className={styles.errorMessage}>
              <div className={styles.textContent}>
                <h5 className={styles.heading}>No content detected on the page</h5>
                <p className={styles.description}>Please add some widgets and publish your changes</p>
              </div>
            </div>
            }

            {(!loading && fetched && pages && _.size(publishedPages) == 0) &&
              <div className={styles.errorMessage}>
                <div className={styles.textContent}>
                  <h5 className={styles.heading}>Page is not published</h5>
                  <p className={styles.description}>Ready to see how your site stacks up? Publish your page to reveal all the metrics!</p>
                </div>
              </div>
            }

            {!loading && performanceMetrics && _.size(publishedPages) !== 0 &&
              <>
                <div className={styles.filterRow}>
                  {!INTERACTIVE_CONTENT_TYPES.includes(storyboardType) ?
                    <aside className={styles.filterContainer}>
                      <label className={styles.filterLabel}>Page Name:</label>
                      <Dropdown className={styles.storboardTypeDropdown}>
                        <Dropdown.Toggle as={PagesDropdown} id="dropdown-custom-components">
                          <TruncatedTextWithTooltip content={_.get(selectedPage, 'name', null)} maxWidth={130}/>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {(pages || []).filter(page => page.published && !page.hidden).map((page)=> { 
                              return (
                                <Dropdown.Item onClick={()=>setSelectedPage(page)} active={page.id == _.get(selectedPage, 'id', null)} key={page.id} eventKey={page.id}>
                                  <TruncatedTextWithTooltip content={page.name} maxWidth={250} placement="right"/>
                                </Dropdown.Item>
                              )
                          })}
                        </Dropdown.Menu>
                      </Dropdown>
                    </aside>
                    :
                    <div className={styles.placeholder}></div>
                  }
                  <aside className={styles.deviceSwitcher}>
                    <Button
                      variant="primary"
                      className={cx(styles.actionButton, {[styles.active] : device === "desktop"})}
                      onClick={() => switchDevice("desktop")}
                    >
                      <i className={cx("cleverstory-laptop", styles.laptop)} />
                      Desktop
                    </Button>
                    <Button
                      active={device === "desktop"}
                      className={cx(styles.actionButton, {[styles.active] : device === "mobile"})}
                      onClick={() => switchDevice("mobile")}
                    >
                      <i className={cx("cleverstory-mobile", styles.mobile)} />
                      Mobile
                    </Button>
                  </aside>
                </div>
                  <div className={styles.statsContainer}>
                    {(pagePerformanceMetrics || []).map((item, index)=>(
                      <StatBox key={index} device={device} order={index+1} item={item} />
                    ))}
                  </div>  
                <footer className={styles.actionsContainer}>
                  <span className={styles.scoringCalculator}>
                    See how your score is determined with the 
                    <a className={styles.infoLink} href="https://googlechrome.github.io/lighthouse/scorecalc/" target="_blank">Scoring Calculator</a>
                  </span>
                  <div className={styles.actions}>
                    {lastTestedDate &&
                      <>
                        <span className={styles.lastTested}>Last tested: {convertDatesToTimezone(lastTestedDate)}</span>
                        <div className={styles.separator}></div>
                      </>
                    }
                    <Button onClick={()=>showHistory(true)} size="sm" className={styles.actionButton} variant="secondary" disabled={loading}> 
                      View History
                    </Button>
                    <Button onClick={()=>handleTestAgain(selectedPage, true)} size="sm" className={styles.actionButton} variant="primary" disabled={loading}>
                      Test Again
                    </Button>
                  </div>
                </footer>
              </>
            }
          </div>
        </main>
      </div>

      <SpeedTestHistoryModal selectedPage={selectedPage} show={viewHistoryModal} onDiscard={()=>showHistory(false)}/>
    </>
  )
}

const StatBox = ({ item, device, order }) => {
  const { title, description, range, score, status, className } = item;

  const mobileModePopover = (
    <Popover id="popover-basic" className={cx(styles.mobileMessage)}>
      <Popover.Body>
        PageSpeed scores for mobile are very low, normally ranging between 20 and 40. 
        Google runs its mobile test on a 3G connection, which is slower than what most 
        visitors use to access your site.
      </Popover.Body>
    </Popover>
  );
  const checkScore = (score && !isNaN(parseFloat(score))) ? score : null;
  return(
    <div className={cx(styles.statBox, styles[className], {[styles.smallCard]: order >= 3})}>
      <div>
        <h6 className={styles.title}>{title}</h6>
        <p className={styles.description}>{description}</p>
        <div className={styles.status} style={checkScore === null && order < 3 ? {paddingTop: '25px'} : {}}>
          <span className={styles.numbers}>{checkScore}</span>
          {device== 'mobile' && order == 1 && 
            <OverlayTrigger 
              trigger={['hover', 'focus']}
              placement="bottom" 
              overlay={mobileModePopover}
              popperConfig={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [130, 0],
                    },
                  },
                ],
              }}
            >
                <span className={styles.info}><i className="cleverstory-info"></i></span>
            </OverlayTrigger>
          }
          <span className={cx(styles.grade, styles[status])}>{status}</span>          
        </div>
      </div>
      
      <div className={styles.rangeGuide} dangerouslySetInnerHTML={{ __html: range }}>
      </div>
    </div>
  )
}

const PagesDropdown = React.forwardRef(({ children, onClick }, ref) => (
  <span
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className={styles.dropDownToggle}
  >
    {children}
    <i className={cx('cleverstory-drop-down')}/>
  </span>
));

const SpeedTestHistoryModal = ({ selectedPage, show, onDiscard }) => {
  const dispatch = useDispatch();

  const loading = useSelector((state) => _.get(state, `storyboards.storyboard.performance.performanceHistory.loading`, false));
  let performanceHistory = useSelector((state) => _.get(state, `storyboards.storyboard.performance.performanceHistory.items`, []));

  if(Array.isArray(performanceHistory)){
    performanceHistory = performanceHistory.sort((a, b) => b.lastTestedDate - a.lastTestedDate);
  }

  useEffect(()=>{
    if(show && _.get(selectedPage, 'id', null)){      
      dispatch(fetchPagePerformanceHistory(selectedPage.id));
    }
  },[show])
  
  return (
    <Modal backdrop='static'
      className={styles.speedTestHistoryModal}
      contentClassName={styles.speedTestHistoryContent}
      dialogClassName={styles.speedTestHistoryDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="sm"
    >
      <button className={styles.closeButton} onClick={onDiscard}>
        <i className={cx('cleverstory-close', styles.closeButtonIcon)} />
      </button>
      <Modal.Body className={styles.speedTestHistoryBody}>
        <header className={styles.header}>
          <h6 className={styles.title}>Page Speed Test History</h6>
          <p className={styles.description}>See your latest test results from Google.</p>
        </header>
        <div className={styles.historyTable}>
          <Row className={cx(styles.tableRow, styles.headerRow)}>
            <Col sm={6} className={styles.column1}>Tested On</Col>
            <Col sm={3} className={styles.column2}>Desktop</Col>
            <Col sm={3} className={styles.column3}>Mobile</Col>
          </Row>
            {loading ?
              <LoadingMessage/>
              :
              <div className={styles.speedResultsContainer}>
                {
                  (performanceHistory || []).map((item)=>{
                    const { desktopPerformance, mobilePerformance, lastTestedDate } = item;
                    
                    return(
                      <Row className={cx(styles.tableRow)}>
                        <Col sm={6} className={styles.column1}>{convertDatesToTimezone(lastTestedDate)}</Col>
                        <Col sm={3} className={styles.column2}>{parseFloat(desktopPerformance) ?  Math.round(desktopPerformance * 100) : 'failed'}</Col>
                        <Col sm={3} className={styles.column3}>{parseFloat(mobilePerformance) ?  Math.round(mobilePerformance * 100) : 'failed'}</Col>
                      </Row>
                    )
                  })
                }
              </div>
            }
        </div>
      </Modal.Body>
    </Modal>
  )
}

const LoadingMessage = () => {
  const ref = useRef(null);

  useEffect(() => {
    ref.current.play();
  }, []);


  return (
    <div className={styles.emptyMetricsMessage}>
      <div className={cx(styles.loaderContainer)}>
        <div className={styles.loaderVideoWrapper}>
          <video ref={ref} className={styles.loaderMessageVideo} loop autoPlay={true} playsInline muted>
            <source src="/images/reportsLoader.mp4" type="video/mp4" />
          </video>
          <label className={styles.loaderMessage}>This might take a few minutes</label>
        </div>
      </div>
    </div>
  )
}

export default PerformanceView

const convertDatesToTimezone = (timestamp) => {
  const dateTime = moment(timestamp);
  const formattedDateTime = dateTime?.format('MMM D, YYYY, h:mm:ss A');
  return formattedDateTime;
}